import { css } from '@emotion/react'
import { useMedia } from 'react-use'
import { useState, useEffect } from 'react'

import { Modal, ModalHeader, ModalBody, ModalFooter } from '../../shared/modal'
import { BP_SMALL_UP } from '../../../lib/styles/breakpoints'
import { TextField } from '../../shared/input'
import { SendEmailInput } from '../../../generated/graphql'
import { SecondaryButton, PrimaryButton } from '../../shared/buttons'
import { ButtonType } from '../../shared/buttons/types'
import { primaryBodyCss, captionTextCss } from '../../../lib/styles/typography'
import ChipInput from '../../shared/input/ChipInput'
import { validateEmail } from '../../../lib/utils'
import RichTextField from '../../shared/input/RichTextField'
import { LabelVisibility } from '../../../types'

const formCss = css`
  margin-top: -8px;
  margin-left: -8px;
  margin-right: -8px;
`

const confirmCss = css`
  margin-left: 12px;
`

const toEmailContainerCss = css`
  position: relative;
  padding: 0px;
  width: 70%;
`

const subjectFieldContainerCss = css`
  position: relative;
  margin-top: 15px;
  width: 70%;

  .subjectTextFieldCss {
    margin-bottom: 0px;
  }
`

const optionalCaptionCss = css`
  ${captionTextCss}
  padding-left: 10px;
`

const messageFieldContainerCss = css`
  position: relative;
  margin-top: 20px;
`

const fieldCss = css`
  ${primaryBodyCss}
`

type EmailStudentModalProps = {
  /** Determines visibility of modal */
  isOpen: boolean
  /** Event callback when user triggers a close event (eg. button click or ESC key) */
  onCancel: () => void
  /** Event callback when user clicks the Send button */
  onSend: (input: SendEmailInput) => void
  /** Default 'To' email address */
  toEmailAddress?: string
}

const EmailStudentModal = ({
  isOpen,
  onCancel,
  onSend,
  toEmailAddress
}: EmailStudentModalProps) => {
  const isNotMobile = useMedia(`(${BP_SMALL_UP})`)
  const [isToEmailEmpty, setToEmailEmpty] = useState(false)
  const [isMessageBodyEmpty, setMessageBodyEmpty] = useState(false)
  const [invalidEmailAddress, setInvalidEmailAddress] = useState(false)

  const [emailState, setEmailState] = useState<SendEmailInput>({
    toEmail: toEmailAddress || '',
    subject: '',
    message: ''
  })

  const { toEmail, subject, message } = emailState

  useEffect(() => {
    setToEmailEmpty(isToEmailEmpty && !toEmail.trim())
  }, [toEmail, isToEmailEmpty])

  const validateMessageBody = () => {
    setMessageBodyEmpty(!message || !message.trim())
  }

  const onToEmailChipsChange = (emailAddresses: string[]) => {
    let emailAddr = ''
    if (emailAddresses && emailAddresses.length) {
      emailAddr = emailAddresses.join(';')
    }
    setEmailState({ ...emailState, toEmail: emailAddr })
    setToEmailEmpty(!emailAddr.trim())
    // If there are email addresses, make sure they're in the correct format (this regex does not cover all possible email addresses)
    setInvalidEmailAddress(emailAddresses && emailAddresses.some((val) => !validateEmail(val)))
  }

  const validateToEmail = () => {
    setToEmailEmpty(!toEmail.trim())
  }

  const onSubjectChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setEmailState({ ...emailState, [event.target.name]: event.target.value })
  }

  const onSubjectKeyDown = (event: React.KeyboardEvent<HTMLElement>) => {
    if (event.key === 'Enter') {
      event.stopPropagation()
      event.preventDefault()
    }
  }

  const onMessageChange = (value: string) => {
    setEmailState({ ...emailState, message: value })
    setMessageBodyEmpty(!value?.trim())
  }

  const onSubmit = (e: React.FormEvent) => {
    validateMessageBody()
    validateToEmail()
    e.preventDefault()
    if (toEmail && message) {
      onSend({
        toEmail,
        subject,
        message
      })
    }
  }

  let toEmailHelperText = ''
  if (invalidEmailAddress) {
    toEmailHelperText = 'The email address not in the expected format'
  } else if (isToEmailEmpty) {
    toEmailHelperText = 'This field is required'
  }

  const isSubmitDisabled = isToEmailEmpty || invalidEmailAddress || isMessageBodyEmpty

  return (
    <Modal ariaLabel="Email Student" allowMobilePage isOpen={isOpen} onSubmit={onSubmit}>
      <ModalHeader allowMobile onClose={onCancel} title="Email Student" />
      <ModalBody allowMobile>
        <div css={formCss}>
          <div css={toEmailContainerCss}>
            <div css={fieldCss}>
              <ChipInput
                error={isToEmailEmpty || invalidEmailAddress}
                delayBeforeAdd
                id="toEmailAddress"
                inputType="email"
                label="Email Address"
                newChipKeys={['Enter', ';', ',']}
                onBlur={validateToEmail}
                onChipsChange={onToEmailChipsChange}
                helperText={toEmailHelperText}
                value={toEmailAddress}
              />
            </div>
          </div>
          <div css={subjectFieldContainerCss}>
            <div css={fieldCss}>
              <TextField
                className="subjectTextFieldCss"
                id="subject"
                label="Subject"
                name="subject"
                onChange={onSubjectChange}
                onKeyDown={onSubjectKeyDown}
                value={subject || ''}
              />
            </div>
            <span css={optionalCaptionCss}>Optional</span>
          </div>
          <div css={messageFieldContainerCss}>
            <div css={fieldCss}>
              <RichTextField
                error={isMessageBodyEmpty}
                helperText={!isMessageBodyEmpty ? '' : 'This field is required'}
                id="message"
                label="Message"
                labelVisibility={LabelVisibility.DEFAULT}
                name="message"
                onBlur={validateMessageBody}
                onChange={onMessageChange}
                rows={5}
                value={message || ''}
              />
            </div>
          </div>
        </div>
      </ModalBody>
      <ModalFooter>
        {isNotMobile && <SecondaryButton onClick={onCancel}>Cancel</SecondaryButton>}
        <PrimaryButton
          css={confirmCss}
          type={ButtonType.Submit}
          disabled={isSubmitDisabled}
          onClick={onSubmit}
        >
          Send
        </PrimaryButton>
      </ModalFooter>
    </Modal>
  )
}

export default EmailStudentModal
